// Registry of every replaceable image slot on the site.
// Each slot has: key (stable id), label (admin display), group, default URL.
// New slots can be added here; the admin panel and the public site both read this list.

import { serviceCategories, blogPosts, brand, clinicHighlights } from '../mock';
import { treatmentGalleries } from '../mock';

const ISTANBUL_FRAMES = [
  'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1920&q=85',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1920&q=85',
  'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=1920&q=85',
  'https://images.unsplash.com/photo-1549445069-d1125f7a129c?w=1920&q=85',
  'https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?w=1920&q=85',
  'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=1920&q=85',
];
const CASABLANCA_FRAMES = [
  'https://images.unsplash.com/photo-1552693673-1bf958298935?w=1920&q=85',
  'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=1920&q=85',
  'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=1920&q=85',
  'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=1920&q=85',
  'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=1920&q=85',
  'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=1920&q=85',
];

export const HERO_DEFAULTS = {
  istanbul: ISTANBUL_FRAMES,
  casablanca: CASABLANCA_FRAMES,
};

const LOC_BG = {
  tr: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=900&q=75',
  ma: 'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=900&q=75',
};

/**
 * Build the full slot list. Returns array of { key, label, group, defaultUrl }.
 */
export function buildAssetSlots() {
  const slots = [];

  // Hero background video (overrides the slideshow when uploaded)
  slots.push({
    key: 'hero.video',
    group: 'Hero — Background video',
    label: 'Hero background video (MP4 / WEBM)',
    defaultUrl: '',
    kind: 'video',
  });

  // Hero slideshow
  ISTANBUL_FRAMES.forEach((url, i) => slots.push({
    key: `hero.istanbul.${i}`, group: 'Hero — Istanbul', label: `Istanbul slide ${i + 1}`, defaultUrl: url,
  }));
  CASABLANCA_FRAMES.forEach((url, i) => slots.push({
    key: `hero.casablanca.${i}`, group: 'Hero — Casablanca', label: `Casablanca slide ${i + 1}`, defaultUrl: url,
  }));

  // Service categories
  serviceCategories.forEach((c) => slots.push({
    key: `cat.${c.id}`, group: 'Service categories', label: c.label, defaultUrl: c.image,
  }));

  // Blog posts
  blogPosts.forEach((p, i) => slots.push({
    key: `blog.${i}`, group: 'Blog posts', label: p.title, defaultUrl: p.image,
  }));

  // Clinic highlights — flatten tab × image
  clinicHighlights.forEach((tab) => {
    tab.images.forEach((url, i) => slots.push({
      key: `clinic.${tab.id}.${i}`, group: `Clinic highlights — ${tab.label}`,
      label: `${tab.label} photo ${i + 1}`, defaultUrl: url,
    }));
  });

  // Locations
  brand.locations.forEach((loc) => slots.push({
    key: `loc.${loc.id}`, group: 'Locations', label: `${loc.city} background`, defaultUrl: LOC_BG[loc.id],
  }));

  // Treatment hero + gallery
  Object.entries(treatmentGalleries).forEach(([slug, g]) => {
    if (g.hero) {
      slots.push({
        key: `tr.${slug}.hero`, group: `Treatment: ${slug}`, label: 'Hero image', defaultUrl: g.hero,
      });
    }
    (g.images || []).forEach((url, i) => slots.push({
      key: `tr.${slug}.gallery.${i}`, group: `Treatment: ${slug}`, label: `Before/After ${i + 1}`, defaultUrl: url,
    }));
  });

  return slots;
}

const memo = { list: null, byKey: null };
export function getSlots() {
  if (!memo.list) {
    memo.list = buildAssetSlots();
    memo.byKey = Object.fromEntries(memo.list.map((s) => [s.key, s]));
  }
  return memo.list;
}
export function getSlotDefault(key) {
  if (!memo.byKey) getSlots();
  return memo.byKey[key]?.defaultUrl || '';
}
